import React from 'react';

// Mode 7-style world map.
// A flat texture (world heightmap rendered to an offscreen canvas) is projected
// in perspective onto a ground plane, scanline-by-scanline. The current location
// pin floats above the surface in 3D, with the camera slowly orbiting.

// Land mass anchor points (lon/lat → degrees), simplified.
// Each entry is a polygon describing a continent outline.
const LAND_POLYGONS = [
  // North America
  [[-168, 65], [-140, 70], [-95, 73], [-75, 60], [-60, 47], [-80, 27], [-97, 25], [-105, 22], [-117, 32], [-130, 50], [-160, 60]],
  // Central / Mexico
  [[-100, 18], [-90, 21], [-83, 9], [-78, 8], [-90, 14]],
  // South America
  [[-78, 12], [-60, 11], [-35, -5], [-40, -22], [-58, -38], [-70, -55], [-72, -45], [-80, -10]],
  // Greenland
  [[-55, 83], [-22, 82], [-20, 70], [-45, 60], [-55, 70]],
  // Europe
  [[-10, 58], [10, 60], [30, 56], [40, 48], [25, 36], [10, 36], [-10, 38], [-10, 50]],
  // Scandinavia
  [[5, 71], [30, 70], [22, 60], [8, 58]],
  // Africa
  [[-17, 21], [12, 32], [25, 32], [35, 30], [42, 12], [50, 11], [40, -5], [40, -25], [25, -34], [18, -34], [10, -8], [-5, 5], [-15, 12]],
  // Middle East
  [[35, 36], [50, 38], [56, 25], [44, 12], [38, 22]],
  // Russia / North Asia
  [[30, 70], [60, 75], [110, 76], [140, 72], [170, 68], [180, 65], [180, 55], [140, 55], [100, 52], [60, 55], [40, 60], [30, 65]],
  // India
  [[68, 24], [78, 27], [88, 25], [89, 21], [82, 8], [76, 8], [70, 18]],
  // China / SE Asia
  [[78, 36], [110, 40], [122, 38], [125, 30], [115, 20], [108, 15], [100, 10], [95, 18], [88, 25], [82, 30]],
  // Indonesia
  [[95, 5], [120, 0], [135, -5], [140, -2], [120, 5], [100, 6]],
  // Australia
  [[113, -22], [129, -12], [142, -11], [153, -28], [148, -38], [128, -33], [115, -34]],
  // Japan
  [[130, 31], [141, 36], [145, 43], [140, 45], [134, 35]],
  // UK / Ireland
  [[-10, 54], [-2, 58], [1, 51], [-5, 50]],
];

const CITIES = {
  'lisbon, portugal': [-9.14, 38.72],
  'lisbon': [-9.14, 38.72],
  'taipei, taiwan': [121.56, 25.03],
  'taipei': [121.56, 25.03],
  'tbilisi, georgia': [44.78, 41.71],
  'bangkok, thailand': [100.50, 13.75],
  'mexico city, mexico': [-99.13, 19.43],
  'lima, peru': [-77.04, -12.05],
  'london, uk': [-0.13, 51.51],
  'london': [-0.13, 51.51],
  'new york, usa': [-74.00, 40.71],
  'san francisco, usa': [-122.42, 37.77],
  'tokyo, japan': [139.69, 35.69],
  'tokyo': [139.69, 35.69],
  'berlin, germany': [13.41, 52.52],
  'berlin': [13.41, 52.52],
  'austin, usa': [-97.74, 30.27],
  'bali, indonesia': [115.09, -8.34],
  'chiang mai, thailand': [98.99, 18.79],
  'medellín, colombia': [-75.56, 6.24],
  'buenos aires, argentina': [-58.38, -34.61],
  'cape town, south africa': [18.42, -33.93],
};

function lookupCity(name) {
  if (!name) return null;
  const key = name.toLowerCase().trim();
  if (CITIES[key]) return CITIES[key];
  const first = key.split(',')[0].trim();
  if (CITIES[first]) return CITIES[first];
  return null;
}

// Convert lon/lat to UV coordinates on the world texture (0..1)
function lonLatToUV(lon, lat) {
  return [(lon + 180) / 360, (90 - lat) / 180];
}

// Render the world heightmap onto an offscreen canvas once.
function renderWorldTexture(W, H) {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');

  // Fill ocean with very dark
  g.fillStyle = '#050505';
  g.fillRect(0, 0, W, H);

  // Faint ocean grid lines (latitude bands)
  g.strokeStyle = 'rgba(255, 255, 255, 0.04)';
  g.lineWidth = 1;
  for (let lat = -60; lat <= 60; lat += 30) {
    const y = ((90 - lat) / 180) * H;
    g.beginPath(); g.moveTo(0, y); g.lineTo(W, y); g.stroke();
  }
  // Equator brighter
  g.strokeStyle = 'rgba(255, 228, 77, 0.10)';
  g.beginPath(); g.moveTo(0, H/2); g.lineTo(W, H/2); g.stroke();

  // Draw land polygons filled, with a gold tint
  LAND_POLYGONS.forEach(poly => {
    g.fillStyle = '#1F1F1A';
    g.beginPath();
    poly.forEach(([lon, lat], i) => {
      const [u, v] = lonLatToUV(lon, lat);
      const x = u * W, y = v * H;
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    });
    g.closePath();
    g.fill();

    // Outline
    g.strokeStyle = 'rgba(255, 228, 77, 0.55)';
    g.lineWidth = 1.5;
    g.stroke();

    // Inner contour rings for "topo" feel
    let cx = 0, cy = 0;
    poly.forEach(([lon, lat]) => {
      const [u, v] = lonLatToUV(lon, lat);
      cx += u * W; cy += v * H;
    });
    cx /= poly.length; cy /= poly.length;

    let maxR = 0;
    poly.forEach(([lon, lat]) => {
      const [u, v] = lonLatToUV(lon, lat);
      const dx = u * W - cx, dy = v * H - cy;
      const d = Math.sqrt(dx*dx + dy*dy);
      if (d > maxR) maxR = d;
    });

    g.save();
    g.beginPath();
    poly.forEach(([lon, lat], i) => {
      const [u, v] = lonLatToUV(lon, lat);
      const x = u * W, y = v * H;
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    });
    g.closePath();
    g.clip();
    for (let r = 1; r <= 4; r++) {
      const ratio = r / 5;
      const radius = maxR * (1 - ratio * 0.8);
      g.strokeStyle = `rgba(255, 228, 77, ${0.10 + ratio * 0.15})`;
      g.lineWidth = 1;
      g.beginPath();
      g.ellipse(cx, cy, radius, radius * 0.85, 0, 0, Math.PI * 2);
      g.stroke();
    }
    g.restore();
  });

  // Lon grid (subtle vertical)
  g.strokeStyle = 'rgba(255, 255, 255, 0.03)';
  for (let lon = -150; lon <= 150; lon += 30) {
    const x = ((lon + 180) / 360) * W;
    g.beginPath(); g.moveTo(x, 0); g.lineTo(x, H); g.stroke();
  }

  return c;
}

function TopoMap({ city, label }) {
  const canvasRef = React.useRef(null);
  const rafRef = React.useRef(0);
  const textureRef = React.useRef(null);
  const [pinScreen, setPinScreen] = React.useState({ x: 0.5, y: 0.5, visible: true, scale: 1 });

  React.useEffect(() => {
    if (!textureRef.current) {
      textureRef.current = renderWorldTexture(2048, 1024);
    }
  }, []);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let W = 0, H = 0;
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    function resize() {
      const r = canvas.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();

    // Camera params (Mode 7 style)
    // Camera looks at the ground plane from above-ish. The horizon is at H * 0.42.
    const horizonRatio = 0.45;
    const cameraHeight = 80;        // vertical distance from ground
    const fov = 1.0;                 // controls how compressed the perspective is

    // Target city UV (normalized 0..1 on the texture). The texture is tiled in U.
    let targetUV = lonLatToUV(0, 30);
    const coords = lookupCity(city);
    if (coords) {
      targetUV = lonLatToUV(coords[0], coords[1]);
    }

    // Camera centers near the target city in U; we slowly drift U for a "gentle pan".
    let camU = targetUV[0];
    let camV = targetUV[1];

    let t = 0;
    function draw() {
      t += 0.005;
      ctx.clearRect(0, 0, W, H);

      // Sky gradient (background above horizon)
      const horizonY = H * horizonRatio;
      const sky = ctx.createLinearGradient(0, 0, 0, horizonY);
      sky.addColorStop(0, '#0A0A0A');
      sky.addColorStop(0.6, '#0F0E08');
      sky.addColorStop(1, '#181508');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, W, horizonY);

      // Stars in sky
      ctx.fillStyle = 'rgba(255, 228, 77, 0.6)';
      for (let i = 0; i < 30; i++) {
        const sx = ((i * 73 + 7) % 100) / 100 * W;
        const sy = ((i * 41) % 100) / 100 * horizonY * 0.85;
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(t * 2 + i));
        ctx.globalAlpha = tw * 0.5;
        ctx.fillRect(sx, sy, 1, 1);
      }
      ctx.globalAlpha = 1;

      // Horizon line + subtle gold glow
      const glow = ctx.createLinearGradient(0, horizonY - 30, 0, horizonY + 4);
      glow.addColorStop(0, 'rgba(255, 228, 77, 0)');
      glow.addColorStop(1, 'rgba(255, 228, 77, 0.25)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, horizonY - 30, W, 32);

      // Slow pan in U
      camU = (targetUV[0] + Math.sin(t * 0.3) * 0.015) % 1;
      if (camU < 0) camU += 1;

      // Mode 7: scanline projection
      const tex = textureRef.current;
      if (!tex) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const TW = tex.width, TH = tex.height;
      const texCtx = tex.getContext('2d');
      const imgData = texCtx.getImageData(0, 0, TW, TH);
      const td = imgData.data;

      // Output buffer
      const out = ctx.createImageData(W, H);
      const od = out.data;

      // For each scanline below horizon
      for (let y = Math.floor(horizonY); y < H; y++) {
        const screenY = (y - horizonY) / (H - horizonY);  // 0 at horizon, 1 at bottom
        // distance from camera (further = smaller screenY)
        // Use 1 / (screenY + tiny) to map to world depth
        const depth = cameraHeight / (screenY * fov + 0.001);

        // Wavy ground perturbation — gentle wave that flows toward viewer
        const wave = Math.sin(depth * 0.04 - t * 4) * 0.0025
                   + Math.sin(depth * 0.08 + t * 2.3) * 0.0015;

        // For each pixel in the scanline
        const rowBase = y * W * 4;
        for (let x = 0; x < W; x++) {
          const screenX = (x / W - 0.5) * 2;  // -1..1
          // Map screen x to texture u offset based on depth
          // farther (small screenY) = wider U range
          const uOffset = screenX * depth * 0.0008;
          let u = (camU + uOffset) % 1;
          if (u < 0) u += 1;
          // V: closer to bottom = closer to camera = closer to camV
          const vBand = camV + (1 - screenY) * 0.001 * depth;
          let v = vBand + wave;
          // Clamp v
          v = Math.max(0.01, Math.min(0.99, v));

          const tx = Math.floor(u * TW) % TW;
          const ty = Math.floor(v * TH);
          const tIdx = (ty * TW + tx) * 4;

          // Distance fog: darken/desaturate by depth
          const fogStrength = Math.min(1, depth / 200);
          const fog = 1 - fogStrength * 0.85;

          const oi = rowBase + x * 4;
          od[oi] = td[tIdx] * fog;
          od[oi + 1] = td[tIdx + 1] * fog;
          od[oi + 2] = td[tIdx + 2] * fog;
          od[oi + 3] = 255;
        }
      }

      // Render the ground using putImageData via temporary canvas (so we can layer above sky)
      const tmp = document.createElement('canvas');
      tmp.width = W; tmp.height = H;
      tmp.getContext('2d').putImageData(out, 0, 0);
      ctx.drawImage(tmp, 0, 0);

      // Wavy contour overlay above the ground (parallax bands)
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, horizonY, W, H - horizonY);
      ctx.clip();
      const contourCount = 18;
      for (let c = 0; c < contourCount; c++) {
        const phase = c / contourCount;
        const flow = (phase + t * 0.2) % 1;
        const yPos = horizonY + flow * (H - horizonY);
        const opacity = 0.10 * (1 - Math.abs(flow - 0.5) * 2) * (flow > 0.05 ? 1 : 0);
        ctx.strokeStyle = `rgba(255, 228, 77, ${opacity})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = 0; i <= 80; i++) {
          const x = (i / 80) * W;
          const yy = yPos + Math.sin(i * 0.12 + t * 3 + c) * 4
                          + Math.sin(i * 0.05 - t * 1.7 + c * 2) * 6;
          if (i === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
        }
        ctx.stroke();
      }
      ctx.restore();

      // Pin always centered on screen
      const pinScreenX = W / 2;
      const pinScreenY = H / 2;

      const visible = true;
      const scale = Math.max(0.5, Math.min(2, 80 / cameraHeight));

      setPinScreen({
        x: pinScreenX / W,
        y: pinScreenY / H,
        visible,
        scale,
      });

      rafRef.current = requestAnimationFrame(draw);
    }
    draw();

    const onResize = () => resize();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [city]);

  return (
    <div className="now__map">
      <canvas ref={canvasRef} />
      {pinScreen.visible && label && label !== 'here' && (
        <div
          className="now__pin"
          style={{
            top: `${pinScreen.y * 100}%`,
            left: `${pinScreen.x * 100}%`,
            transform: `translate(-50%, -100%) scale(${pinScreen.scale})`,
          }}
        >
          <div className="now__pin-stem" />
          <div className="now__pin-dot" />
          <div className="now__pin-label">{label}</div>
        </div>
      )}
      <div className="now__map-coords">
        MODE 7 · LIVE · {city || 'unknown'}
      </div>
      <div className="now__map-scanlines" />
    </div>
  );
}

export default TopoMap;
